import { z } from 'zod';
import { isoDateTimeSchema, looseInteger, nullableText } from '../common/coercion.js';
import { janisIdFromSapDocumentReference } from '../sap/primitives.js';
import { altanetCodeSchema, altanetDecimal, altanetDocumentNumberSchema, altanetLineNumber, altanetUserSchema, } from './primitives.js';
/**
 * ############################################################################
 * # PROPUESTA v1 — NO EXISTE TODAVIA.                                        #
 * ############################################################################
 *
 * El aviso de picking que le PROPONEMOS a Altanet (2026-10-21), en reemplazo de
 * la propuesta v0 (`picking-webhook.ts`). Es lo que tiene que mandar el webhook
 * que Altanet se comprometio a construir el 2026-08-26, escrito con los nombres
 * de campo de Altanet. No es documentacion del proveedor: es nuestro pedido, y
 * se le entrega en `docs/f9-picking-wms.md` («Formato propuesto a Altanet»).
 *
 * Responde lo que pide `docs/flujo-venta-facturacion.md` («Lo que necesitamos
 * del webhook del WMS») y cierra las preguntas N1-N6 de
 * `docs/preguntas-altanet.md`:
 *
 * - **N1** (catalogo de estados): `Estado_pedido` y `Estado_linea` son catalogos
 *   CERRADOS (`z.enum`), a diferencia del catalogo abierto de la v0. Un estado
 *   nuevo es un cambio de contrato, no una sorpresa en produccion.
 * - **N2** (cantidades por linea): `Detalle` es obligatorio y va COMPLETO en cada
 *   aviso, con `Cantidad_pickeada` ACUMULADA (nunca incrementos): un aviso
 *   perdido no descuadra nada, el siguiente trae el total.
 * - **N3** (timestamp del WMS): `Fecha_evento` en ISO 8601 CON offset (la v0 usaba
 *   la fecha de Altanet sin zona) y `Secuencia`, creciente por documento: con
 *   ella se descarta un aviso viejo que llega tarde.
 * - **N4** (identificadores): `Linea_base` es el `LineNum` de la factura de
 *   reserva en SAP; `DocEntry_respaldo` (y opcional `DocNum_respaldo`) es esa
 *   factura; `NumAtCard_respaldo` es su `NumAtCard`, que lleva el id del pedido
 *   de Janis, o el del GRUPO en una venta agrupada. Ese es el hilo con nuestro
 *   pedido.
 * - **N5** (URL propia): no es un campo; el aviso va a su propio endpoint.
 * - **N6** (reintentos): `Id_evento` es IGUAL en cada reintento de un mismo aviso
 *   y unico por (`Empresa`, `Bodega`). La clave de deduplicacion lo combina con
 *   la orden de despacho (`altanetPickingNoticeDedupeKey`). Altanet reintenta
 *   mientras no respondamos 2xx.
 *
 * `Entrega_ERP` es opcional: el resultado del envio de la entrega a SAP que hace
 * el WMS, si lo informa (lo usara S5).
 *
 * ## Una OD por bodega y fecha (acordado con Altanet el 2026-10-28)
 *
 * La interfaz SAP → WMS arma UNA orden de despacho por (bodega, fecha de
 * picking) con las lineas de la factura de reserva, y la libera en esa fecha
 * (`docs/f9-picking-wms.md`, «Ventas agrupadas»). Una factura de reserva puede
 * tener entonces VARIAS OD, y la forma del aviso no cambia; cambia lo que
 * significan tres campos:
 *
 * - `Detalle` son las lineas de ESA OD: un subconjunto de la factura. «Completo
 *   en cada aviso» vale para la OD, no para la factura.
 * - `NumAtCard_respaldo` es el de la factura de reserva, compartido por todas
 *   sus OD. En una venta agrupada (`docs/pedidos-de-tienda.md` §1.4) es el de
 *   la VENTA: `<PREFIJO>_<orderGroupId>`, con el id del grupo de pedidos de
 *   Janis, de la misma forma que el de un pedido (24 hex). El texto no los
 *   distingue: `classifyAltanetPickingNoticeReference` nunca descarta uno como
 *   OD ajena, y F9 busca el id primero entre las ventas agrupadas.
 * - `Linea_base` sigue siendo el `LineNum` de la factura de reserva: con el F9
 *   encuentra el pedido HIJO de cada linea (`line_xref`).
 *
 * Lo que RECIBIMOS es `passthrough`: un campo nuevo del proveedor se conserva en
 * vez de mandar el aviso a cuarentena. Y es tolerante donde el proveedor es
 * ambiguo y ya sabemos por que:
 *
 * - **Un opcional que llega `""` o `null` vale como ausente** (`vacioComoAusente`,
 *   y `nullableText` en los textos): Altanet manda `""` cuando el dato no aplica
 *   (`altanetOptionalDate`). Un `DocNum` vacio en una entrega RECHAZADA, o un
 *   `Mensaje: null`, no puede tirar el aviso entero: el receptor responde 2xx y
 *   Altanet no lo reintenta.
 * - **`NumAtCard_respaldo` es texto libre**: en SAP lo es, y Altanet avisa TODAS
 *   las OD. Si la OD es del middleware lo decide
 *   `classifyAltanetPickingNoticeReference`, no el borde.
 */
/**
 * Un opcional del aviso: `""`, solo espacios y `null` valen como AUSENTE; un
 * valor de verdad se valida con `schema`. Solo para lo que RECIBIMOS.
 */
function vacioComoAusente(schema) {
    return z.preprocess((value) => value === null || (typeof value === 'string' && value.trim() === '') ? undefined : value, schema.optional());
}
/** Version del aviso. La v0 no traia el campo. */
export const ALTANET_PICKING_NOTICE_VERSION = '1';
/** Estados del pedido (la orden de despacho) en el WMS. Catalogo CERRADO. */
export const ALTANET_PICKING_ORDER_STATUSES = [
    'RECIBIDO',
    'EN_PICKING',
    'PICKEADO',
    'PICKEADO_CON_FALTANTE',
    'ANULADO',
];
/** Estados de cada linea. Catalogo CERRADO. */
export const ALTANET_PICKING_LINE_STATUSES = [
    'PENDIENTE',
    'EN_PICKING',
    'PICKEADA',
    'FALTANTE',
];
/** Estados del pedido despues de los cuales el WMS ya no deberia mandar avisos. */
export const ALTANET_PICKING_TERMINAL_ORDER_STATUSES = [
    'PICKEADO',
    'PICKEADO_CON_FALTANTE',
    'ANULADO',
];
/**
 * Claves que solo existen en la v1. Un objeto con CUALQUIERA de ellas que no
 * cumple la v0 se valida como v1, aunque le falte `Version`: asi la cuarentena
 * dice que falta `Version` en vez de reclamar los campos de la v0 (ver
 * `altanetPickingWebhookSchema`).
 */
export const ALTANET_PICKING_V1_ONLY_KEYS = [
    'Version',
    'Id_evento',
    'Secuencia',
    'DocEntry_respaldo',
    'NumAtCard_respaldo',
];
/** Las cantidades se comparan a 4 decimales, la escala de `numeric(18,4)`. */
function round4(value) {
    return Math.round(value * 10_000) / 10_000;
}
/** Una linea del aviso: lo que pide la factura de reserva y lo pickeado hasta ahora. */
export const altanetPickingNoticeLineV1Schema = z
    .object({
    /**
     * `LineNum` de la linea en la factura de reserva de SAP (base 0). En una
     * venta agrupada dice de que pedido hijo es la linea (`line_xref`).
     */
    Linea_base: altanetLineNumber(),
    /** `ItemCode` de SAP. */
    Producto: z
        .string()
        .trim()
        .min(1, 'La linea no trae SKU.')
        .max(64, 'El SKU no puede superar 64 caracteres.'),
    /** Cantidad de la linea en la factura de reserva, en unidades de SAP. */
    Cantidad_solicitada: altanetDecimal().refine((value) => value > 0, 'La cantidad solicitada debe ser mayor que cero.'),
    /** Lo pickeado ACUMULADO de la linea, nunca el incremento de este aviso. */
    Cantidad_pickeada: altanetDecimal({ min: 0 }),
    Estado_linea: z.enum(ALTANET_PICKING_LINE_STATUSES),
    /**
     * Por que la linea quedo con faltante. Obligatorio si `Estado_linea` es
     * `FALTANTE` (`""` y `null` no son un motivo).
     */
    Motivo_incidencia: nullableText(255),
})
    .passthrough()
    .superRefine((line, ctx) => {
    const pedido = round4(line.Cantidad_solicitada);
    const pickeado = round4(line.Cantidad_pickeada);
    const enCantidad = (message) => {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['Cantidad_pickeada'], message });
    };
    if (pickeado > pedido) {
        enCantidad(`Se pickearon ${String(pickeado)} unidades de ${line.Producto} y solo se pidieron ${String(pedido)}.`);
        return;
    }
    if (line.Estado_linea === 'PENDIENTE' && pickeado !== 0) {
        enCantidad(`Una linea PENDIENTE no puede tener ${String(pickeado)} pickeadas.`);
    }
    if (line.Estado_linea === 'EN_PICKING' && pickeado >= pedido) {
        enCantidad('Una linea EN_PICKING tiene lo pickeado por debajo de lo pedido: si no, es PICKEADA.');
    }
    if (line.Estado_linea === 'PICKEADA' && pickeado !== pedido) {
        enCantidad(`Una linea PICKEADA tiene todo lo pedido (${String(pedido)}) y trae ${String(pickeado)}.`);
    }
    if (line.Estado_linea === 'FALTANTE') {
        if (pickeado >= pedido) {
            enCantidad('Una linea FALTANTE tiene lo pickeado por debajo de lo pedido.');
        }
        if (line.Motivo_incidencia === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['Motivo_incidencia'],
                message: 'Una linea FALTANTE tiene que traer el motivo de la incidencia.',
            });
        }
    }
});
/** Resultado del envio de la entrega a SAP que hace el WMS. Opcional. */
export const altanetPickingErpDeliverySchema = z
    .object({
    Estado: z.enum(['ENVIADA', 'RECHAZADA']),
    /** `DocEntry` de la entrega creada en SAP. Una RECHAZADA no lo tiene. */
    DocEntry: vacioComoAusente(looseInteger({ min: 1 })),
    DocNum: vacioComoAusente(looseInteger({ min: 1 })),
    /** El error de SAP, si la entrega se rechazo. */
    Mensaje: nullableText(500),
    Fecha: vacioComoAusente(isoDateTimeSchema),
})
    .passthrough();
/** El aviso de picking v1. Un aviso por orden de despacho, nunca arreglos. */
export const altanetPickingNoticeV1Schema = z
    .object({
    Version: z.literal(ALTANET_PICKING_NOTICE_VERSION),
    /**
     * Id del aviso. IGUAL en cada reintento; unico por (`Empresa`, `Bodega`), y
     * la clave lo combina igual con la orden de despacho (N4 abierta).
     */
    Id_evento: z
        .string()
        .regex(/^[A-Za-z0-9._:-]{1,128}$/, 'El Id_evento admite letras, digitos y . _ : - (hasta 128 caracteres).'),
    /** Creciente por documento: un aviso con una secuencia menor es viejo. */
    Secuencia: looseInteger({ min: 1 }),
    /** Momento del cambio en el WMS, ISO 8601 CON offset. */
    Fecha_evento: isoDateTimeSchema,
    Empresa: altanetCodeSchema,
    Bodega: altanetCodeSchema,
    /** `OD` = orden de despacho. */
    Documento: altanetCodeSchema,
    Numero_documento: altanetDocumentNumberSchema,
    /** Tipo del documento de respaldo en el ERP: `FR`, la factura de reserva. */
    Documento_respaldo: altanetCodeSchema,
    /** `DocEntry` de la factura de reserva en SAP. */
    DocEntry_respaldo: looseInteger({ min: 1 }),
    /**
     * `DocNum` de la factura de reserva, opcional. Nombre NUEVO a proposito: en
     * la v0, `Numero_documento_respaldo` llevaba el correlation id. En la v1 el
     * hilo con el pedido es `NumAtCard_respaldo`.
     */
    DocNum_respaldo: vacioComoAusente(looseInteger({ min: 1 })),
    /**
     * `NumAtCard` de la factura de reserva, tal cual: en un pedido del
     * middleware es `MKPL_<pedido de Janis>` (o `JANIS_`), y es el hilo. En una
     * venta agrupada es el de la VENTA (`<PREFIJO>_<orderGroupId>`), el mismo
     * en todas las OD de la factura.
     *
     * 🔴 Se recibe como TEXTO LIBRE (`null` y `""` quedan en `""`): en SAP lo es,
     * y una OD ajena (una OC del cliente, una venta manual, una factura sin
     * `NumAtCard`) no es un aviso mal formado. Exigir aca la convencion
     * `<PREFIJO>_<id>` la mandaba a cuarentena, y con el 2xx Altanet no la
     * reintenta. Si es nuestra lo decide `classifyAltanetPickingNoticeReference`.
     * La clave si es obligatoria: sin ella el aviso no trae el hilo.
     */
    NumAtCard_respaldo: z
        .string({
        required_error: 'El aviso tiene que traer NumAtCard_respaldo (vacio si no hay).',
        invalid_type_error: 'NumAtCard_respaldo es texto.',
    })
        .trim()
        .max(100, 'NumAtCard no puede superar 100 caracteres.')
        .nullable()
        .transform((value) => value ?? ''),
    Estado_pedido: z.enum(ALTANET_PICKING_ORDER_STATUSES),
    Descripcion_estado: nullableText(255),
    Usuario: vacioComoAusente(altanetUserSchema),
    /**
     * El detalle COMPLETO de la OD, en cada aviso. Con una OD por bodega y
     * fecha, un subconjunto de las lineas de la factura de reserva.
     */
    Detalle: z
        .array(altanetPickingNoticeLineV1Schema)
        .min(1, 'El aviso tiene que traer el detalle completo de la orden.'),
    Entrega_ERP: vacioComoAusente(altanetPickingErpDeliverySchema),
})
    .passthrough()
    .superRefine((notice, ctx) => {
    const lineas = new Set();
    notice.Detalle.forEach((line, indice) => {
        if (lineas.has(line.Linea_base)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['Detalle', indice, 'Linea_base'],
                message: `La linea base ${String(line.Linea_base)} va dos veces en el aviso.`,
            });
        }
        lineas.add(line.Linea_base);
    });
    const estadoInvalido = (indice, message) => {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['Detalle', indice, 'Estado_linea'],
            message,
        });
    };
    switch (notice.Estado_pedido) {
        case 'RECIBIDO':
            notice.Detalle.forEach((line, indice) => {
                if (line.Estado_linea !== 'PENDIENTE') {
                    estadoInvalido(indice, 'Un pedido RECIBIDO tiene todas sus lineas PENDIENTE.');
                }
            });
            break;
        case 'PICKEADO':
            notice.Detalle.forEach((line, indice) => {
                if (line.Estado_linea !== 'PICKEADA') {
                    estadoInvalido(indice, 'Un pedido PICKEADO tiene todas sus lineas PICKEADA: con un faltante es PICKEADO_CON_FALTANTE.');
                }
            });
            break;
        case 'PICKEADO_CON_FALTANTE':
            notice.Detalle.forEach((line, indice) => {
                if (line.Estado_linea !== 'PICKEADA' && line.Estado_linea !== 'FALTANTE') {
                    estadoInvalido(indice, 'Un pedido PICKEADO_CON_FALTANTE tiene sus lineas PICKEADA o FALTANTE.');
                }
            });
            if (!notice.Detalle.some((line) => line.Estado_linea === 'FALTANTE')) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['Detalle'],
                    message: 'Un pedido PICKEADO_CON_FALTANTE tiene al menos una linea FALTANTE.',
                });
            }
            break;
        case 'EN_PICKING':
        case 'ANULADO':
            break;
    }
});
/**
 * Clave de deduplicacion del aviso v1:
 * `altanet:picking:v1:<Empresa>:<Bodega>:<Documento>:<Numero_documento>:<Id_evento>`.
 *
 * 🔴 Lleva la bodega Y la orden de despacho porque todavia no sabemos de que es
 * unico `Id_evento` (pregunta N4 abierta). Un WMS que numere los eventos por
 * documento (`1`, `2`, `3` en cada OD) haria chocar el aviso de una OD con el de
 * otra, y el receptor descartaria el segundo como `duplicate` con un 2xx: una
 * perdida silenciosa de un dato que Altanet no reenvia. Un reintento del mismo
 * aviso lleva el mismo documento, asi que agregarlo no rompe la deduplicacion.
 *
 * Pide solo los campos de la clave: la simulacion del WMS la arma para buscar
 * un aviso ya entregado antes de tener el aviso completo.
 */
export function altanetPickingNoticeDedupeKey(notice) {
    return [
        'altanet',
        'picking',
        'v1',
        notice.Empresa,
        notice.Bodega,
        notice.Documento,
        notice.Numero_documento,
        notice.Id_evento,
    ].join(':');
}
const TERMINAL = new Set(ALTANET_PICKING_TERMINAL_ORDER_STATUSES);
/** `true` si el aviso cierra la orden de despacho (pickeada, con faltante o anulada). */
export function isAltanetPickingNoticeTerminal(notice) {
    return TERMINAL.has(notice.Estado_pedido);
}
/** Lineas que quedaron con faltante. */
export function altanetPickingNoticeShortageLines(notice) {
    return notice.Detalle.filter((line) => line.Estado_linea === 'FALTANTE');
}
/** Clasifica el `NumAtCard_respaldo` del aviso (ver `AltanetPickingNoticeReference`). Pura. */
export function classifyAltanetPickingNoticeReference(notice) {
    const janisId = janisIdFromSapDocumentReference(notice.NumAtCard_respaldo);
    return janisId === null
        ? { kind: 'foreign', numAtCard: notice.NumAtCard_respaldo }
        : { kind: 'middleware', janisId };
}
/**
 * El id de Janis del aviso, sacado del `NumAtCard` de la factura de reserva.
 * `null` si la orden de despacho no es del middleware (POS, proyectos).
 *
 * @deprecated 🔴 Pese al nombre, el id puede ser el de un GRUPO de pedidos (una
 * venta agrupada), no solo el de un pedido. Usar
 * `classifyAltanetPickingNoticeReference`, que lo dice.
 */
export function altanetPickingNoticeJanisOrderId(notice) {
    const referencia = classifyAltanetPickingNoticeReference(notice);
    return referencia.kind === 'middleware' ? referencia.janisId : null;
}
/**
 * Hilo del aviso en el message log: el id de Janis si es nuestro (el del
 * pedido, o el del grupo en una venta agrupada: todas las OD de la venta
 * comparten el hilo); si no, el `NumAtCard` tal cual, para que el aviso igual se
 * pueda buscar; y sin `NumAtCard`, el documento del WMS
 * (`<Documento>-<Numero_documento>`).
 */
export function altanetPickingNoticeCorrelationId(notice) {
    const referencia = classifyAltanetPickingNoticeReference(notice);
    if (referencia.kind === 'middleware') {
        return referencia.janisId;
    }
    return notice.NumAtCard_respaldo !== ''
        ? notice.NumAtCard_respaldo
        : `${notice.Documento}-${notice.Numero_documento}`;
}
/**
 * `true` si el payload CRUDO tiene la forma de un aviso v1: un objeto con
 * cualquiera de `ALTANET_PICKING_V1_ONLY_KEYS`. No valida nada: decide contra
 * que version se valida.
 */
export function isAltanetPickingNoticePayloadV1(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    return ALTANET_PICKING_V1_ONLY_KEYS.some((key) => Object.hasOwn(value, key));
}
//# sourceMappingURL=picking-notice.js.map